// Bttf: Bark To The Future — Game Constants
// Merged from lib/game/core/constants.ts + lib/game/core/scene-tuning.ts

import { TEX } from "../core/keys"
import type { BumperEffectType } from "../types"

// ── Game dimensions ───────────────────────────────────────────

export const GAME_WIDTH = 960
export const GAME_HEIGHT = 540

// ── Physics ───────────────────────────────────────────────────

export const GRAVITY = 9.5
export const FLAP_POWER = -9
export const MAX_FALL_SPEED = 20

// ── Pinball mechanics — force field ───────────────────────────

export const FORCE_FIELD_ENABLED = false
export const FORCE_FIELD_X = GAME_WIDTH * 0.6

// ── Pinball mechanics — slingshots ────────────────────────────

/** Kick angle from horizontal (radians) for slingshot deflectors */
export const SLINGSHOT_KICK_ANGLE = Math.PI / 5.5 // ~32°
/**
 * Speed multiplier applied to ball on slingshot hit.
 * Now persists visibly because applySpeedScaling uses gentle decay, not instant snap.
 */
export const SLINGSHOT_KICK_SPEED_MULT = 1.65
/** Interval between slingshot spawns (ms) */
export const SLINGSHOT_SPAWN_INTERVAL = 9000
/** Slingshot lifetime before de-spawning (ms) */
export const SLINGSHOT_LIFETIME = 14000

// ── Pinball mechanics — left-wall kickback ────────────────────

/** Speed multiplier for the kickback boost when ball hits the left wall */
export const KICKBACK_SPEED_MULT = 1.4
/** Extra lifetime restored to the ball on a kickback (ms) */
export const KICKBACK_LIFETIME_RESTORE = 1800

// ── Pinball ball speed dynamics ───────────────────────────────

/**
 * Per-frame decay factor applied when the ball is above its target speed.
 * Value close to 1 = slow decay. At 0.994 per frame (~60fps), a speed boost
 * decays ~30% in 1 second and ~50% in 2 seconds — enough to feel impactful
 * without the ball staying fast forever.
 */
export const BALL_OVERSPEED_DECAY = 0.994

/**
 * Minimum exit speed from a round bumper, as a multiple of current base speed.
 * Real pinball bumpers always pop the ball out at a consistent speed regardless
 * of how slow the ball was when it arrived.
 */
export const BUMPER_POP_SPEED_MULT = 1.35

// ── Bumper combo / powered-up tuning ─────────────────────────

export const COMBO_TARGET_HITS = 5
export const OVERDRIVE_DURATION_MS = 5000
export const SHIELD_DURATION_MS = 7000
export const FIREBALL_DURATION_MS = 7000
export const HEAL_DURATION_MS = 7000
export const FREEZE_DURATION_MS = 5500
export const OVERDRIVE_TIME_SCALE = 0.25
export const FREEZE_TIME_SCALE = 0.45
export const OVERDRIVE_AUTO_FIRE_DELAY_MS = 500
export const FIREBALL_AURA_RADIUS = 112
export const FIREBALL_PULSE_RADIUS = 60
export const FIREBALL_PULSE_INTERVAL_MS = 2000
export const FIREBALL_BOSS_DAMAGE_RATIO = 0.08

// ── Lane system ──────────────────────────────────────────────

export const LANE_TOP_Y = GAME_HEIGHT * 0.33
export const LANE_MID_Y = GAME_HEIGHT * 0.66

export const BUMPER_TYPES = [
  {
    radius: 20,
    color: "#F472B6",
    colorHex: 0xf472b6,
    points: 0,
    textureKey: TEX.BUMPER_COMBO,
    effect: "charge" as BumperEffectType,
  },
  {
    radius: 38,
    color: "#F59E0B",
    colorHex: 0xf59e0b,
    points: 0,
    textureKey: TEX.BUMPER_BREAKER,
    effect: "bank" as BumperEffectType,
  },
  {
    radius: 34,
    color: "#22c55e",
    colorHex: 0x22c55e,
    points: 0,
    textureKey: TEX.BUMPER_SALVAGE,
    effect: "levelup" as BumperEffectType,
  },
  {
    radius: 24,
    color: "#22D3EE",
    colorHex: 0x22d3ee,
    points: 0,
    textureKey: TEX.BUMPER_STASIS,
    effect: "heal" as BumperEffectType,
  },
] as const

export const SLINGSHOT_TYPE = {
  radius: 16,
  color: "#84cc16",
  colorHex: 0x84cc16,
  points: 0,
  textureKey: TEX.BUMPER_SLINGSHOT,
  effect: "charge" as BumperEffectType,
  displayWidth: 52,
  displayHeight: 30,
  bodyOffsetX: 10,
  bodyOffsetY: 7,
} as const

// ── Bumper sizes ─────────────────────────────────────────────

export const BUMPER_SIZE_DEFS = {
  small: { radius: 20, spawnWeight: 0.5, points: 100 },
  medium: { radius: 32, spawnWeight: 0.35, points: 50 },
  large: { radius: 44, spawnWeight: 0.15, points: 25 },
} as const

export const BUMPER_PLAYER_HIT_COOLDOWN = 500

// ── Ball types ───────────────────────────────────────────────

export type BallType = "fireball"

interface BallTypeDef {
  hits: number
  lifetime: number
  cooldown: number
  damageMultiplier: number
  textureKey: string
  displaySize: number
  color: number
}

export const BALL_TYPE_DEFS: Record<BallType, BallTypeDef> = {
  fireball: {
    hits: 1,
    lifetime: 8000,
    cooldown: 1000,
    damageMultiplier: 1.0,
    textureKey: TEX.BALL_BASE,
    displaySize: 10,
    color: 0xf97316,
  },
} as const

export const MAX_ACTIVE_BALLS = 2

// ── Ball definition (replaces launcher abstraction) ───────────

export interface BallDefinition {
  type: "defaultBall"
  baseDamage: number
  ballSpeed: number
  hits: number
  launchAngle: number
  redirectStrength: number
  recoveryDuration: number
  reboundBehavior: string
  launcherClasses: ["ballistic"]
  ballType: BallType
  color: string
  projectileTexture: string
}

export const DEFAULT_BALL_DEF: BallDefinition = {
  type: "defaultBall",
  baseDamage: 20,
  ballSpeed: 6,
  hits: 1,
  launchAngle: 0,
  redirectStrength: 0.65,
  recoveryDuration: 1400,
  reboundBehavior: "stable",
  launcherClasses: ["ballistic"],
  ballType: "fireball",
  color: "#f97316",
  projectileTexture: TEX.BALL_BASE,
}

// ── Player ────────────────────────────────────────────────────

export const PLAYER_WIDTH = 45
export const PLAYER_HEIGHT = 40
export const PLAYER_X = 120
export const INVULNERABILITY_TIME = 1500

// ── Waves ─────────────────────────────────────────────────────

export const MAX_WAVES = 12

// Boss waves: 3, 6, 9, 12
export const BOSS_WAVES = new Set([3, 6, 9, 12])

export const getWaveDuration = (wave: number): number => {
  if (wave >= 12) return 120000
  if (BOSS_WAVES.has(wave)) return 90000
  return (50 + Math.max(0, wave - 1) * 8) * 1000
}

export const getExpToNextLevel = (level: number): number => {
  if (level <= 1) {
    return 12
  }

  if (level <= 5) {
    return 12 + level * 14
  }

  const midRamp = 30 + level * 11
  const scalingTax = Math.floor(level ** 1.22 * 4)

  return midRamp + scalingTax
}

// ── Bumpers ───────────────────────────────────────────────────

export const BUMPER_SPAWN_INTERVAL = 3000
export const BUMPER_LIFETIME = 15000
export const BASE_BOUNCE_FORCE = 250

// ── Bumper charge system ──────────────────────────────────────

// ── Rarity colors ─────────────────────────────────────────────

export const RARITY_COLORS = {
  common: "#9ca3af",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  legendary: "#f59e0b",
}

// ── End-game messages ─────────────────────────────────────────

export const VICTORY_MESSAGES = [
  "EARTH IS SAVED! WHO'S A GOOD BOY? YOU ARE!",
  "The robots have fled! Treats for EVERYONE!",
  "Humanity is free! Time for the BIGGEST belly rubs!",
  "You did it! The vet said you couldn't. LOOK AT YOU NOW!",
]

export const DEFEAT_MESSAGES = [
  "Even good boys need respawns sometimes...",
  "The robots got lucky. Demand a rematch!",
  "You fought like the goodest boy...",
  "Don't worry, there's always more robots to bork at!",
]

// ── Bumper progression ──────────────────────────────────────────

export const BUMPER_PROGRESSION = {
  levelupWave: 4,
  bankWave: 11,
  healWave: 10,
  slingshotWave: 1,
}

// ── Scene tuning ──────────────────────────────────────────────

export const SCENE_TUNING = {
  // Player physics
  playerGravityScale: 105,
  playerBounceX: 0.1,
  playerBounceY: 0.15,
  playerMaxVelocityX: 220,
  playerMaxFallVelocityScale: 30,
  playerBoundsMarginX: 30,
  playerBoundsMarginTop: 10,
  playerBoundsMarginRight: 60,
  playerBoundsMarginBottom: 20,
  playerBodyWidth: 51,
  playerBodyHeight: 42,
  playerBodyOffsetX: 15,
  playerBodyOffsetY: 12,
  playerTopBoundaryY: 10,
  playerBottomBoundaryY: GAME_HEIGHT - 40,

  initialScrollSpeed: 4.5,
  dangerScrollSpeedStep: 0.25,
  readyEmitDelayMs: 100,
  frameNormalizationMs: 16,
  parallaxSpeeds: [0.08, 0.65, 1.1, 1.8],
  playerHomeXRatio: 0.15,
  playerHorizontalCorrectionGain: 0.16,
  playerHorizontalCorrectionClamp: 24,
  playerHorizontalDamping: 0.9,
  playerHorizontalVelocityMin: -120,
  playerHorizontalVelocityMax: 190,
  bumperPlayerRearmPadding: 70,
  bumperPlayerLaunchSeparation: 8,
  bumperPlayerMinLaunchSpeed: 420,
  fallAccelerationPerMs: 0.15,
  maxFallVelocityScale: 28,
  invulnerabilityFlashStepMs: 80,
  invulnerabilityFlashAlpha: 0.4,
  shieldPulseDivisor: 80,
  shieldPulseAmount: 0.06,
  fireballPulseDivisor: 60,
  fireballPulseAmount: 0.1,
  fireballTrailOffsetX: 8,
  fireballTrailParticles: 1,
  enemyScrollFactor: 0.45,
  enemyLeftBoundaryX: 0,
  enemyOffscreenCleanupX: -50,
  enemyFireActivationX: 40,
  enemyScalePulseDivisor: 180,
  enemyScalePulseAmount: 0.02,
  enemySeparationForce: 0.06,
  enemySeparationRadius: 48,
  launcherMinimumCooldownMs: 70,
  bouncingBallBoundsPadding: 24,
  bouncingBallMaxBounces: 6,
  projectileTrailChance: 0.5,
  projectileTrailOffsetX: 10,
  projectileCleanupMargin: 20,
  expCoinAttractRadius: 100,
  expCoinAttractSpeed: 4,
  expCoinAttractSpeedRangeFactor: 0.01,
  expCoinScrollFactor: 0.5,
  expCoinFloatDivisor: 20,
  expCoinFloatAmplitude: 0.3,
  expCoinCleanupX: -20,
  harvestMagnetStartProgress: 0.82,
  harvestMagnetRadiusMultiplier: 1.8,
  harvestMagnetSpeedMultiplier: 1.7,
  bumperScrollFactor: 1.2,
  bumperSeekMarginY: 70,
  bumperSeekLerp: 0.014,
  bumperWobbleDivisor: 240,
  bumperWobbleAmplitude: 0.6,
  bumperCleanupX: -50,
  bumperPulseDivisor: 200,
  bumperPulseAmount: 0.1,
  bumperSafetyInvulnerabilityMs: 900,
  bumperSafetyThreatClearRadius: 96,
  comboMultiplierStep: 0.5,
  baseCoinDrop: 1,
  coinDropWaveScale: 0.15,
  flapSoundMs: 200,
  flapForceScale: 38,
  flapVelocityCarryFactor: 0.1,
  flapHorizontalBoost: 36,
  flapSparkCount: 3,
  flapSparkOffsetX: 15,
  flapSparkOffsetY: 10,
  launcherProjectileOriginOffsetX: 28,
  projectileSpeedStatScale: 0.5,
  launcherLevelDamageStep: 0.16,
  launcherMinimumDamageMultiplier: 0.2,
  launcherMinimumAttackSpeedMultiplier: 0.25,
  critScale: 1.16,
  critTint: 0xfacc15,
  enemyProjectileOriginOffsetX: 18,
  enemyProjectileSpeed: 6.5,
  enemyBossProjectileDamage: 12,
  enemyDefaultProjectileDamage: 8,
  enemySpawnOffsetX: 50,
  enemySpawnIntroAlpha: 0,
  enemySpawnIntroScale: 0.88,
  enemyDisplayScale: 1.2,
  enemySpawnTweenDurationMs: 220,
  bumperSpawnBaseOffsetX: 80,
  bumperSpawnVarianceX: 120,
  bumperSpawnVerticalMargin: 100,
  bumperSpawnVerticalSafeZone: 200,
  bumperDashBodyWidthScale: 1.5,
  bumperDashBodyHeightScale: 0.7,
  bumperDashBodyOffsetXScale: 0.25,
  bumperDashBodyOffsetYScale: 0.65,
  bumperCircleBodyScale: 0.72,
  bumperCircleBodyOffsetScale: 0.28,
  bumperCircleBodyPositionOffsetScale: 0.28,
  bumperVelocityBaseScale: 0.85,
  bumperVelocityVariance: 0.7,
  bumperWobbleSeedRange: 1000,
  bumperTargetYOffsetMin: -110,
  bumperTargetYOffsetMax: 110,
  bumperIntroScale: 0,
  bumperIntroTweenDurationMs: 300,
  bumperExitScale: 0,
  bumperExitAlpha: 0,
  bumperExitTweenDurationMs: 200,
  shieldPowerDurationMs: 2600,
  shieldInvulnerabilityFloorMs: 900,
  shieldFlashAlpha: 0.22,
  shieldFlashDurationMs: 160,
  fireballPowerDurationMs: 2200,
  fireballHorizontalBoost: 100,
  fireballHorizontalMin: -180,
  fireballHorizontalMax: 260,
  fireballFlashAlpha: 0.26,
  fireballFlashDurationMs: 180,
  dashHorizontalVelocity: 220,
  dashVerticalVelocityScale: 0.7,
  dashVerticalVelocityMin: -220,
  dashVerticalVelocityMax: 220,
  dashFlashAlpha: 0.2,
  dashFlashDurationMs: 120,
  dashLaunchHorizontalForce: 320,
  dashLaunchVerticalFloor: -140,
  dashLaunchNudgeX: 18,
  dashLaunchClampMarginX: 48,
  nearbyProjectileClearPadding: 8,
  fireballEnemyTouchRadius: 36,
  defeatedEnemyExplosionParticles: 18,
  defeatedEnemySparkParticles: 8,
  waveGuardFlashAlpha: 0.24,
  waveGuardFlashDurationMs: 140,
  duplicateTreatBurstParticles: 10,
  bumperTreatBurstParticles: 6,
  waveSummaryEnemyStaggerMs: 95,
  waveSummaryCoinStaggerMs: 65,
  waveSummaryFlightDurationMs: 900,
  waveSummaryEnemyShrinkDurationMs: 120,
  waveSummaryOpenShopBufferMs: 700,
  waveSummaryHudTargetX: 58,
  waveSummaryHudTargetY: 104,
  waveSummaryRewardScale: 1.1,
  activeEnemyBaseCap: 10,
  activeEnemyCapPerWave: 1,
  activeEnemyCapBossBonus: 4,
  activeEnemyCapMax: 28,
  pushDistanceRateBase: 12,
  waveIntroDurationMs: 2200,
  waveIntroStartX: -120,
  waveIntroEndX: GAME_WIDTH * 0.38,
  waveIntroY: GAME_HEIGHT * 0.46,
} as const

export const ENEMY_SPAWN_TUNING = {
  topMargin: 50,
  bottomMargin: 150,
  horizontalSpawnOffset: 50,
  swarmVariance: 2,
  swarmVarianceCenterOffset: 0.5,
} as const

// ── Sprite sizes ──────────────────────────────────────────────

export const SPRITE_SIZES: Record<string, { w: number; h: number }> = {
  player_sprite: { w: 68, h: 55 },
}

// ── Projectile sizes ──────────────────────────────────────────

export const PROJECTILE_SIZES: Record<string, { w: number; h: number }> = {
  proj_bark: { w: 28, h: 28 },
  proj_laser: { w: 36, h: 12 },
  proj_drool: { w: 22, h: 22 },
  proj_missile: { w: 40, h: 24 },
  proj_howl: { w: 44, h: 28 },
  proj_boomerang: { w: 32, h: 24 },
  proj_mine: { w: 24, h: 24 },
  proj_enemy: { w: 20, h: 20 },
  proj_fireball: { w: 20, h: 20 },
  proj_spark: { w: 42, h: 20 },
  proj_charged: { w: 36, h: 24 },
  proj_fish_fireball: { w: 36, h: 36 },
  proj_neon_ring: { w: 48, h: 48 },
  proj_plasma_bolt: { w: 24, h: 24 },
}
